// Imports => React
import {useEffect, useRef, useState, useMemo, useCallback} from 'react';
import {Fade} from 'react-awesome-reveal';
import {Props} from './ac-select-box.proptypes';
import clsx from 'clsx';
import _ from 'lodash';

// Imports => Constants
import { TYPES } from '@asdf-constants';

// Imports => Utilities
import {
  AcUUID,
  AcIsSet,
  AcIsUndefined,
  AcIsBoolean,
  AcIsEmptyString,
  AcSanitize,
} from '@asdf-utils';

const _CLASSES = {
  MAIN: 'ac-select-box',
  NAVIGATOR: 'ac-select-box--menu',
  HAS_VALUE: 'ac-select-box--selected',
  INPUT: {
    MAIN: 'ac-select-box__field',
    LABEL: 'ac-select-box__field-label',
    LABEL_ELLIPSE: 'ac-select-box__field-label--ellipse',
    LABEL_EMPTY: 'ac-select-box__field-label--empty',
  },
  DISABLED: 'ac-select-box--disabled',
  LABEL: 'ac-select-box__label',

  OPEN: 'ac-select-box--open',
  ERROR: 'ac-select-box--error',
  LIST: {
    MAIN: 'ac-select-box__list',
    ITEM: 'ac-select-box__list__item',
    ITEM_STATIC: 'ac-select-box__list__item--static',
    ITEM_SELECTED: 'ac-select-box__list__item--selected',
    ITEM_DISABLED: 'ac-select-box__list__item--disabled',
  },
  VALIDATION: {
    ERROR: 'ac-select-box__error',
  },
  INSTRUCTIONS: 'ac-select-box__instructions',
};

let _tryAndFindDelay = null;
let _tryAndFindSequence = '';
const isMobile = 'ontouchstart' in document.documentElement && window.outerWidth < 768;

// Component
const AcSelectBox = ({
  value,
  label = 'Selectbox label',
  name = 'selectbox',
  placeholder = 'Choose an option',
  instructions,
  disabled = false,
  required = false,
  menu,
  type = TYPES.SELECT,
  options = [],
  callback,
  validation,
  maxOptions = 8,
  className,
  reference = AcUUID(),
  ellipseLongChosenOption = false,
  forceShowError = false,
  innerRef,
}) => {
  const [open, setOpen] = useState(false);
  const selectRef = useRef();
  const [error, setError] = useState(null);

  const $selectbox = useRef(null);
  const $label = useRef(null);
  const $list = useRef(null);

  const toggleDelay = useRef(null);

  const getErrorClassNames = useMemo(() => {
    return clsx(_CLASSES.VALIDATION.ERROR);
  }, []);

  const getInstructionsClassNames = useMemo(() => {
    return clsx(_CLASSES.INSTRUCTIONS);
  }, []);

  const getListItemClassNames = value => {
    const selected = getSelectedOption && getSelectedOption.value === value;
    return clsx(_CLASSES.LIST.ITEM, selected && _CLASSES.LIST.ITEM_SELECTED);
  };

  const getListClassNames = useMemo(() => {
    return clsx(_CLASSES.LIST.MAIN);
  }, []);

  const getLabelClassNames = useMemo(() => {
    return clsx(_CLASSES.LABEL);
  }, []);

  const getInputLabelClassNames = useMemo(() => {
    const empty = AcIsUndefined(value) || AcIsEmptyString(value);
    return clsx(
      _CLASSES.INPUT.LABEL,
      empty && _CLASSES.INPUT.LABEL_EMPTY,
      ellipseLongChosenOption && _CLASSES.INPUT.LABEL_ELLIPSE
    );
  }, [value, ellipseLongChosenOption]);

  const getBoxLabelClassNames = useMemo(() => {
    return clsx(_CLASSES.INPUT.MAIN);
  }, []);

  const getMainClassNames = useMemo(() => {
    return clsx(
      _CLASSES.MAIN,
      disabled && _CLASSES.DISABLED,
      open && _CLASSES.OPEN,
      error && _CLASSES.ERROR,
      placeholder && _CLASSES.PLACEHOLDER,
      value && _CLASSES.HAS_VALUE,
      menu && _CLASSES.NAVIGATOR,
      className
    );
  }, [value, disabled, error, open, placeholder, menu, className]);

  useEffect(() => {
    if (forceShowError) {
      let err = false;

      if (validation) {
        err = validation(name, value, required, type);
        setError(err);
      }
    }
  }, [forceShowError, validation]);

  useEffect(() => {
    removeEvents().then(() => {
      addEvents();

      setTimeout(() => {
        if (open === true) {
          const $firstoption = $list.current.querySelector('li');
          if ($firstoption & $firstoption.focus) {
            $firstoption.focus();
          }
        }
      }, 100);
    });

    return () => removeEvents();
  }, [open]);

  const addEvents = () => {
    const nativeEl = 'ontouchstart' in document.documentElement && window.outerWidth < 768;
    if (nativeEl) {
      document.addEventListener('keyup', handleKeyUp, { passive: true });
      document.addEventListener('click', hide, false);
    }
  };

  const removeEvents = () => {
    return new Promise(resolve => {
      const nativeEl = 'ontouchstart' in document.documentElement && window.outerWidth < 768;
      if (nativeEl) {
        document.removeEventListener('keyup', handleKeyUp, { passive: true });
        document.removeEventListener('click', hide, false);
      }
      resolve();
    });
  };

  const handleScrollListToOption = item => {
    if (AcIsSet(item)) {
      setTimeout(() => {
        window.requestAnimationFrame(() => {
          const pos = item.offsetTop;
          if (AcIsSet(pos) && $list.current) {
            $list.current.scrollTop = pos - 20;
          }
        });
      }, 1000 / 60);
    }
  };

  const handleTryAndFocusOption = key => {
    setTimeout(() => {
      window.requestAnimationFrame(() => {
        if ($list && $list.current) {
          const $activeElement = document.activeElement;

          if (AcIsSet($activeElement) && $activeElement.tagName.toLowerCase() !== 'li') {
            // no focus yet, let's focus on the first options
            const $firstoption = $list.current.querySelector('li');
            if ($firstoption & $firstoption.focus) {
              $firstoption.focus();
              handleScrollListToOption($firstoption);
            }
          } else if (key === 38 || key === 'ArrowUp') {
            // prev option
            const $prev = $activeElement.previousElementSibling;

            if (AcIsSet($prev) && $prev.focus) {
              // $prev.focus();
              handleScrollListToOption($prev);
            }
          } else if (key === 40 || key === 'ArrowDown') {
            // next option
            const $next = $activeElement.nextElementSibling;

            if (AcIsSet($next) && $next.focus) {
              $next.focus();
              handleScrollListToOption($next);
            }
          }
        }
      });
    }, 1000 / 60);
  };

  const handleTryAndFindMatchingOption = (key, num) => {
    setTimeout(() => {
      window.requestAnimationFrame(() => {
        if (
          !(
            (num > 64 && num < 91) ||
            (num > 47 && num < 58) ||
            (num > 95 && num < 112) ||
            key === ' '
          )
        ) {
          return;
        }

        if (_tryAndFindDelay) {
          clearTimeout(_tryAndFindDelay);
        }

        _tryAndFindSequence += key;

        if ($list.current && $list.current) {
          const $options = $list.current.querySelectorAll('li');

          if (AcIsSet($options)) {
            const len = $options.length;
            let n = 0;
            let found = null;

            for (n; n < len; n++) {
              const $item = $options[n];
              let text = $item.textContent;

              if (AcIsSet(text)) {
                text = text.toLowerCase();
                const query = _tryAndFindSequence.toLowerCase();

                if (text.indexOf(query) > -1) {
                  if ($item.focus) {
                    $item.focus();
                  }
                  found = $item;
                  break;
                }
              }
            }

            handleScrollListToOption(found);
          }
        }

        _tryAndFindDelay = setTimeout(() => {
          _tryAndFindSequence = '';
        }, 600);
      });
    }, 1000 / 60);
  };

  const handleKeyUp = event => {
    setTimeout(() => {
      window.requestAnimationFrame(() => {
        if (event && event.persist) {
          event.persist();
        }
        if (event && event.persist) {
          event.persist();
        }

        let inside = false;

        if ($selectbox && $selectbox.current) {
          inside = $selectbox.current.contains(event.target);
        }

        if (inside) {
          const key = event.key || event.which;

          const spacebarAndNotOpen = (key === 32 || key === 'Spacebar' || key === ' ') && !open;

          const $activeElement = document.activeElement;
          const enterAndNotOpen = (key === 13 || key === 'Enter') && !open;

          if (spacebarAndNotOpen || enterAndNotOpen) {
            if (AcIsSet($activeElement) && $activeElement.tagName.toLowerCase() !== 'li') {
              handleToggle(true);
            }
          }

          if (key) {
            switch (key) {
              case 'Escape':
              case 27:
                handleToggle(false);
                break;

              case 38:
              case 'ArrowUp':
              case 40:
              case 'ArrowDown':
                handleTryAndFocusOption(key);
                break;

              default:
                handleTryAndFindMatchingOption(key, event.which);
            }
          }
        }
      });
    }, 1000 / 60);
  };

  const hide = () => {
    setTimeout(() => {
      window.requestAnimationFrame(() => {
        if (event && event.persist) {
          event.persist();
        }

        const nativeEl = 'ontouchstart' in document.documentElement && window.outerWidth < 768;
        const $selectEl = document.querySelector(`[id="${reference}"]`);
        if (nativeEl && $selectEl) {
          $selectEl.setAttribute('size', 1);
          // $selectEl.focus();
        } else if (event && event.target) {
          const $element = $selectbox.current;

          if ($element) {
            const inside = $element.contains(event.target);

            if (!inside) {
              handleToggle(false);
            }
          }
        } else {
          handleToggle(false);
        }
      });
    }, 1000 / 60);
  };

  const handleToggle = state => {
    window.requestAnimationFrame(() => {
      if (state && state.persist) {
        state.persist();
      }
      if (state && state.stopPropagation) {
        state.stopPropagation();
      }
      if (state && state.preventDefault) {
        state.preventDefault();
      }

      const options = parsedOptionsList;
      if (disabled || options.length === 0) {
        return;
      }

      const nativeEl = 'ontouchstart' in document.documentElement && window.outerWidth < 768;

      setOpen(state === false ? false : !open);

      if (nativeEl) {
        const $selectEl = document.querySelector(`[id="${reference}"]`);
        if ($selectEl) {
          $selectEl.setAttribute('size', !open ? options.length : 1);
        }
      }
    });
  };

  const handleToggleByKey = state => {
    setTimeout(() => {
      window.requestAnimationFrame(() => {
        const nativeEl = 'ontouchstart' in document.documentElement && window.outerWidth < 768;
        if (!nativeEl) {
          if (state && state.persist) {
            state.persist();
          }
          if (state && state.stopPropagation) {
            state.stopPropagation();
          }
          if (state && state.preventDefault) {
            state.preventDefault();
          }

          const key = state.key || state.which;

          if (AcIsSet(key) && key !== 1 && (key !== 13 || key !== 'Enter')) {
            return;
          }

          const options = parsedOptionsList;
          if (disabled || options.length === 0) {
            return;
          }

          const status = AcIsSet(state) && AcIsBoolean(state) ? state : !open;

          setOpen(status);
        }
      });
    }, 1000 / 60);
  };

  const handleClick = (event, item) => {
    (document.activeElement as HTMLElement).blur();
    if (event && event.persist) {
      event.persist();
    }

    const value = item.value;
    let err = false;

    if (validation) {
      err = validation(name, value, required, type);
      setError(err);
    }

    handleCallback(event, name, value, type);
    hide();
  };

  const handleCallback = (event, name, value, type) => {
    if (callback) {
      callback(event, name, value, type);
    }
  };

  const parsedOptionsList = useMemo(() => {
    return options;
  }, [options]);

  const getSelectedOption = useMemo(() => {
    const collection = parsedOptionsList;
    const len = collection.length;
    let n = 0;
    let result = null;

    for (n; n < len; n++) {
      const option = collection[n];

      if (options && option.value === value) {
        result = option;
        break;
      }
    }

    return result;
  }, [value, parsedOptionsList]);

  const getSelectedLabel = useMemo(() => {
    if (AcIsUndefined(value) || AcIsEmptyString(value)) {
      return placeholder;
    }
    if (!AcIsSet(getSelectedOption)) {
      return placeholder;
    }
    if (menu) {
      return placeholder;
    }

    return getSelectedOption && getSelectedOption.name;
  }, [getSelectedOption, placeholder, menu]);

  const renderOptions = useMemo(() => {
    const collection = parsedOptionsList;
    const len = collection.length;
    let n = 0;
    let result = [];

    for (n; n < len; n++) {
      const item = options[n];

      const object = item.disabled ? (
        <li
          key={`select-box-${name}-item-${item.value}`}
          className={`${getListItemClassNames(item.value)} ${_CLASSES.LIST.ITEM_DISABLED}`}
          role={'option'}
          aria-selected={getSelectedOption && getSelectedOption.value === item.value}
          dangerouslySetInnerHTML={{
            __html: AcSanitize(item.name),
          }}
        />
      ) : (
        <li
          key={`select-box-${name}-item-${item.value}`}
          onKeyUp={event => {
            if (!AcIsSet(event) || !AcIsSet(event.key)) {
              return;
            }
            if (event.key === 13 || event.key === 'Enter') {
              handleClick(event, item);
            }
          }}
          onClick={event => handleClick(event, item)}
          className={getListItemClassNames(item.value)}
          role={'option'}
          aria-selected={getSelectedOption && getSelectedOption.value === item.value}
          dangerouslySetInnerHTML={{
            __html: AcSanitize(item.name),
          }}
        />
      );

      result.push(object);
    }

    return result;
  }, [parsedOptionsList, getSelectedOption, value, name]);

  const renderLabel = useMemo(() => {
    if (label === null) return null;
    return (
      <div
        id={`ac-select-box-${reference}-label`}
        className={getLabelClassNames}
        dangerouslySetInnerHTML={{
          __html: label,
        }}
      />
    );
  }, [label]);

  const renderError = useMemo(() => {
    return (
      <div
        className={getErrorClassNames}
        dangerouslySetInnerHTML={{
          __html: error,
        }}
      />
    );
  }, [error]);

  const renderInstructions = useMemo(() => {
    return (
      <div
        className={getInstructionsClassNames}
        dangerouslySetInnerHTML={{
          __html: instructions,
        }}
      />
    );
  }, [instructions]);

  const calculatedListHeight = useMemo(() => {
    const collection = parsedOptionsList;
    const len = collection.length;
    const available = Math.min(len, maxOptions);

    let height = 0;

    if ($list.current && $list.current.childNodes) {
      const children = Array.prototype.slice.call($list.current.childNodes);
      children.slice(0, available).forEach(node => (height += node.scrollHeight));
    }

    return height > 0 ? height / 10 + 0.2 : height;
  }, [parsedOptionsList, maxOptions, open]);

  const getListInlineStyles = useMemo(() => {
    const nativeEl = 'ontouchstart' in document.documentElement && window.outerWidth < 768;

    if (nativeEl) {
      return { height: 0, display: 'none' };
    }

    return { height: open ? `${calculatedListHeight}rem` : '0rem' };
  }, [calculatedListHeight, parsedOptionsList, open]);

  const renderInputElement = useMemo(() => {
    const nativeEl = 'ontouchstart' in document.documentElement && window.outerWidth < 768;

    if (nativeEl) {
      const collection = parsedOptionsList;

      const opts = collection.map(item => (
        <option key={item.uid} value={item.value}>
          {item.name}
        </option>
      ));
      opts.unshift(<option />);

      return (
        <select
          name={name}
          id={reference}
          autoFocus
          value={value}
          disabled={disabled}
          tabIndex={0}
          ref={selectRef}
          onClick={event => {
            if (event && event.persist) {
              event.persist();
            }
            if (event && event.stopPropagation) {
              event.stopPropagation();
            }
          }}
          onChange={event => {
            handleClick(event, { value: event.target.value });
            hide();
          }}
        >
          {opts}
        </select>
      );
    } else {
      <input
        type={'text'}
        name={name}
        id={reference}
        defaultValue={value}
        disabled={disabled}
        tabIndex={0}
      />;
    }
  }, [reference, name, value, disabled]);

  return (
    <div
      ref={e => {
        innerRef && innerRef.ref(e);
        $selectbox.current = e;
      }}
      onMouseDown={e => !isMobile && e.preventDefault()}
      onClick={
        !isMobile
          ? e => {
              e.stopPropagation();
              e.preventDefault();
              handleToggle(e);
            }
          : null
      }
      onFocus={e => {
        e.stopPropagation();
        e.preventDefault();
        if (!isMobile) {
        handleToggle(e);
        }
      }}
      onBlur={e => {
        if (!open) return;
        handleToggle(e);
      }}
      tabIndex={0}
      disabled={disabled}
      onKeyUp={handleToggleByKey}
      className={getMainClassNames}
      role={'combobox'}
      htmlFor={reference}
      aria-roledescription={'input'}
      aria-labelledby={`ac-select-box-${reference}-label`}
      aria-expanded={open}
      aria-owns={`ac-select-box-${reference}-listbox`}
      aria-controls={`ac-select-box-${reference}-listbox`}
      aria-haspopup={'listbox'}
    >
      {renderLabel}
      {instructions && renderInstructions}
      {renderInputElement}
      <div htmlFor={reference} ref={$label} className={getBoxLabelClassNames}>
        <div className={getInputLabelClassNames}>
          <Fade duration={200} key={`select-box-value-${getSelectedLabel}`}>
            <span
              dangerouslySetInnerHTML={{
                __html: getSelectedLabel,
              }}
            />
          </Fade>
        </div>
        <ul
          id={`ac-select-box-${reference}-listbox`}
          ref={$list}
          className={getListClassNames}
          style={getListInlineStyles}
          aria-labelledby={`ac-select-box-${reference}-label`}
          role={'listbox'}
        >
          {renderOptions}
        </ul>
      </div>
      {error && renderError}
    </div>
  );
};

AcSelectBox.displayName = 'AcSelectBox';
AcSelectBox.propTypes = Props;

export { AcSelectBox };
export default AcSelectBox;
